import {
  EXP_IN_SERVICE_TENANT_ID,
  expInServiceTenant,
  ZOOCASA_TENANT_ID,
  zoocasaTenant,
  EXP_REALTY_CA_TENANT_ID,
  expRealtyCATenant,
  EXP_REALTY_US_TENANT_ID,
  expRealtyUSTenant,
  EXP_COMMERCIAL_TENANT_ID,
  expCommercialTenant,
} from './definitions';
import { ThemeNames as TenantNames } from 'types/themes';
import { CountryCodeList } from 'types/countries';

import type { ThemeConfig } from 'themes/themeConfig';
import type { Tenant } from './types';
import type { TenantId } from './definitions';

/**
 * Retrieves a tenant configuration by its ID.
 *
 * This function returns the tenant configuration for a given tenant ID. The tenant ID must be one of
 * the valid tenant identifiers defined in the system:
 * - {@link ZOOCASA_TENANT_ID} - Zoocasa tenant for Canadian market
 * - {@link EXP_REALTY_CA_TENANT_ID} - eXp Realty Canada tenant
 * - {@link EXP_REALTY_US_TENANT_ID} - eXp Realty US tenant
 * - {@link EXP_COMMERCIAL_TENANT_ID} - eXp Commercial tenant
 * - {@link EXP_IN_SERVICE_TENANT_ID} - eXp In-Service tenant
 *
 * Each tenant configuration includes:
 * - Theme configuration
 * - Localization settings (languages, countries, i18n namespaces)
 * - Header and footer configurations
 * - Agent search settings
 * - Debug options
 *
 * @example
 * ```typescript
 * // Get Zoocasa tenant configuration
 * const zoocasaTenant = getTenantById(ZOOCASA_TENANT_ID);
 *
 * // Get eXp Realty Canada tenant configuration
 * const expRealtyCATenant = getTenantById(EXP_REALTY_CA_TENANT_ID);
 * ```
 *
 * @param id The ID of the tenant to retrieve. Must be a valid {@link TenantId}.
 * @returns The tenant configuration object of type {@link Tenant}.
 */
export function getTenantById(id: TenantId): Tenant<unknown> {
  switch (id) {
  case EXP_IN_SERVICE_TENANT_ID:
    return expInServiceTenant;
  case ZOOCASA_TENANT_ID:
    return zoocasaTenant;
  case EXP_REALTY_CA_TENANT_ID:
    return expRealtyCATenant;
  case EXP_REALTY_US_TENANT_ID:
    return expRealtyUSTenant;
  case EXP_COMMERCIAL_TENANT_ID:
    return expCommercialTenant;
  }
  return undefined;
}

/**
 * Retrieves a tenant configuration by its debug header value for testing and QA purposes.
 *
 * This function allows impersonating different tenants during development and testing by returning a tenant configuration
 * based on a specific debug header value. Each tenant defines its own debug header value in their
 * `debug.forceTenantHeaderValue` property and a client can use this function to retrieve the
 * tenant configuration by passing the debug header value to this function.
 *
 * This is particularly useful for:
 * - Testing tenant-specific features
 * - QA verification of tenant configurations
 * - Debugging tenant-related issues
 * - Local development of tenant-specific functionality
 *
 * @example
 * ```typescript
 * // Example header value in HTTP request
 * // X-Debug-Header: "use-exp-in-service"
 * const tenant = getTenantByDebugHeaderValue("use-exp-in-service");
 * ```
 *
 * @param debugHeaderValue - The debug header value specified in the tenant's debug configuration
 * @returns The matching tenant configuration or undefined if no tenant matches the debug header value
 * @see {@link Tenant} for the tenant configuration type
 */
export function getTenantByDebugHeaderValue(debugHeaderValue: string): Tenant<ThemeConfig> | undefined {
  if (debugHeaderValue === expInServiceTenant.debug.forceTenantHeaderValue) {
    return expInServiceTenant;
  }
  return undefined;
}

export function getContentPageHostFromTenant(tenantName?: TenantNames, siteCountryLocation?: string) {
  switch (tenantName) {
  case TenantNames.EXP_REALTY_CA:
    return 'https://exp-ca.content.zoocasa.com';
  case TenantNames.EXP_REALTY_US:
    return 'https://exp-us.content.zoocasa.com';
  case TenantNames.EXP_IN_SERVICE:
    return 'https://inservice.content.zoocasa.com';
  case TenantNames.EXP_COMMERCIAL:
    return 'https://exp-commercial.content.zoocasa.com';
  case TenantNames.ZOOCASA:
  default:
    return siteCountryLocation === CountryCodeList.UNITED_STATES ?
      'https://zoo-us.content.zoocasa.com' : 'https://zoo-ca.content.zoocasa.com';
  }
}